import { type IActiveElectionData } from '@interface/IElection';
import { useCallback, useMemo, useState } from 'react';

export type ElectionType =
	| 'Presidential'
	| 'Governorship'
	| 'Senate'
	| 'House of Rep'
	| 'House of Assembly'
	| 'Local Government'
	| 'Councilorship';

export type ElectionStatus = 'active' | 'upcoming' | 'closed';

export interface ElectionFilters {
	searchQuery: string;
	electionTypes: ElectionType[];
	status: ElectionStatus | null;
	dateFrom: string | null;
	dateTo: string | null;
	stateId: number | null;
	lgaId: number | null;
	wardId: number | null;
	constituency: string;
}

export interface ElectionTypeOption {
	value: ElectionType;
	label: string;
}

export const ELECTION_TYPES: ElectionTypeOption[] = [
	{ value: 'Presidential', label: 'Presidential' },
	{ value: 'Governorship', label: 'Governorship' },
	{ value: 'Senate', label: 'Senate' },
	{ value: 'House of Rep', label: 'House of Rep' },
	{ value: 'House of Assembly', label: 'House of Assembly' },
	{ value: 'Local Government', label: 'Local Government' },
	{ value: 'Councilorship', label: 'Councilorship' },
];

export const STATUS_OPTIONS: { value: ElectionStatus | null; label: string }[] = [
	{ value: null, label: 'All' },
	{ value: 'active', label: 'Active' },
	{ value: 'upcoming', label: 'Upcoming' },
	{ value: 'closed', label: 'Closed' },
];

const initialFilters: ElectionFilters = {
	searchQuery: '',
	electionTypes: [],
	status: null,
	dateFrom: null,
	dateTo: null,
	stateId: null,
	lgaId: null,
	wardId: null,
	constituency: '',
};

export interface ParsedElection {
	name: string;
	date: string;
	location: string;
	formattedDate: string;
	electionType: ElectionType | string;
	status: ElectionStatus;
	stateId?: number;
	lgaId?: number;
	wardId?: number;
}

/**
 * Enhanced parseElectionName to extract election type more accurately
 */
export const parseElectionName = (fullName: string): ParsedElection => {
	// Input: "Governorship election - 2022-07-16 - OSUN"
	const parts = fullName.split(' - ');
	const name = parts[0] || fullName;
	const dateStr = parts[1] || '';
	const location = parts[2] || '';

	const nameLower = name.toLowerCase();

	// Extract election type from name
	let electionType: ElectionType | string = 'Election';
	if (nameLower.includes('presidential')) {
		electionType = 'Presidential';
	} else if (nameLower.includes('governorship')) {
		electionType = 'Governorship';
	} else if (nameLower.includes('senatorial') || nameLower.includes('senate')) {
		electionType = 'Senate';
	} else if (nameLower.includes('house of rep')) {
		electionType = 'House of Rep';
	} else if (nameLower.includes('house of assembly') || nameLower.includes('state assembly')) {
		electionType = 'House of Assembly';
	} else if (nameLower.includes('local government') || nameLower.includes('local govt')) {
		electionType = 'Local Government';
	} else if (nameLower.includes('councilorship') || nameLower.includes('councillorship')) {
		electionType = 'Councilorship';
	}

	// Format date: "2022-07-16" → "Jul 16, 2022"
	let formattedDate = dateStr;
	let status: ElectionStatus = 'active';

	if (dateStr) {
		const date = new Date(dateStr);
		if (!isNaN(date.getTime())) {
			formattedDate = date.toLocaleDateString('en-US', {
				month: 'short',
				day: 'numeric',
				year: 'numeric',
			});

			// Determine status based on date
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const electionDate = new Date(dateStr);
			electionDate.setHours(0, 0, 0, 0);

			if (electionDate > today) {
				status = 'upcoming';
			} else if (electionDate < today) {
				status = 'closed';
			} else {
				status = 'active';
			}
		}
	}

	return { name, date: dateStr, location, formattedDate, electionType, status };
};

/**
 * Hook for managing election filter state and applying filters
 */
export const useElectionFilters = (elections: IActiveElectionData[] | undefined) => {
	const [filters, setFilters] = useState<ElectionFilters>(initialFilters);
	const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

	// Calculate active filter count
	const activeFilterCount = useMemo(() => {
		let count = 0;
		if (filters.searchQuery) count++;
		if (filters.electionTypes.length > 0) count += filters.electionTypes.length;
		if (filters.status) count++;
		if (filters.dateFrom) count++;
		if (filters.dateTo) count++;
		if (filters.stateId) count++;
		if (filters.lgaId) count++;
		if (filters.wardId) count++;
		if (filters.constituency) count++;
		return count;
	}, [filters]);

	// Calculate advanced filter count
	const advancedFilterCount = useMemo(() => {
		let count = 0;
		if (filters.dateFrom) count++;
		if (filters.dateTo) count++;
		if (filters.stateId) count++;
		if (filters.lgaId) count++;
		if (filters.wardId) count++;
		if (filters.constituency) count++;
		return count;
	}, [filters]);

	// Update a single filter
	const updateFilter = useCallback(<K extends keyof ElectionFilters>(key: K, value: ElectionFilters[K]) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
	}, []);

	// Toggle an election type in the multi-select
	const toggleElectionType = useCallback((type: ElectionType) => {
		setFilters((prev) => {
			const exists = prev.electionTypes.includes(type);
			return {
				...prev,
				electionTypes: exists ? prev.electionTypes.filter((t) => t !== type) : [...prev.electionTypes, type],
			};
		});
	}, []);

	// Clear all election types
	const clearElectionTypes = useCallback(() => {
		setFilters((prev) => ({ ...prev, electionTypes: [] }));
	}, []);

	// Select all election types
	const selectAllElectionTypes = useCallback(() => {
		setFilters((prev) => ({
			...prev,
			electionTypes: ELECTION_TYPES.map((t) => t.value),
		}));
	}, []);

	// Remove a specific filter
	const removeFilter = useCallback((key: keyof ElectionFilters, value?: string) => {
		setFilters((prev) => {
			if (key === 'electionTypes' && value) {
				return {
					...prev,
					electionTypes: prev.electionTypes.filter((t) => t !== value),
				};
			}
			return {
				...prev,
				[key]: key === 'electionTypes' ? [] : key === 'searchQuery' || key === 'constituency' ? '' : null,
			};
		});
	}, []);

	// Clear all filters
	const clearAllFilters = useCallback(() => {
		setFilters(initialFilters);
	}, []);

	// Reset advanced filters only
	const resetAdvancedFilters = useCallback(() => {
		setFilters((prev) => ({
			...prev,
			dateFrom: null,
			dateTo: null,
			stateId: null,
			lgaId: null,
			wardId: null,
			constituency: '',
		}));
	}, []);

	// Cascading reset for location filters
	const resetLgaAndBelow = useCallback(() => {
		setFilters((prev) => ({
			...prev,
			lgaId: null,
			wardId: null,
		}));
	}, []);

	const resetWard = useCallback(() => {
		setFilters((prev) => ({
			...prev,
			wardId: null,
		}));
	}, []);

	// Filter matching logic
	const filteredElections = useMemo(() => {
		if (!elections) return [];

		return elections.filter((election) => {
			const parsed = parseElectionName(election.name);

			// Search query filter
			if (filters.searchQuery) {
				const query = filters.searchQuery.toLowerCase().trim();
				const matchesSearch =
					election.name.toLowerCase().includes(query) ||
					parsed.location.toLowerCase().includes(query) ||
					parsed.electionType.toLowerCase().includes(query);
				if (!matchesSearch) return false;
			}

			// Election type filter (multi-select)
			if (filters.electionTypes.length > 0) {
				const matchesType = filters.electionTypes.some((type) => parsed.electionType.toLowerCase() === type.toLowerCase());
				if (!matchesType) return false;
			}

			// Status filter
			if (filters.status && parsed.status !== filters.status) {
				return false;
			}

			// Date range filter
			if (filters.dateFrom || filters.dateTo) {
				if (!parsed.date) return false;

				const electionDate = new Date(parsed.date);
				if (isNaN(electionDate.getTime())) return false;

				if (filters.dateFrom) {
					const fromDate = new Date(filters.dateFrom);
					if (electionDate < fromDate) return false;
				}

				if (filters.dateTo) {
					const toDate = new Date(filters.dateTo);
					if (electionDate > toDate) return false;
				}
			}

			// Location filter (state) - matches by location name since we parse from election name
			if (filters.stateId) {
				// We'll need to match by state name from the parsed location
				// This is a basic implementation; real implementation would need state name lookup
			}

			// Constituency filter
			if (filters.constituency) {
				const query = filters.constituency.toLowerCase().trim();
				if (!election.name.toLowerCase().includes(query)) {
					return false;
				}
			}

			return true;
		});
	}, [elections, filters]);

	// Get list of active filters for display
	const activeFilters = useMemo(() => {
		const list: { key: keyof ElectionFilters; label: string; value?: string }[] = [];

		if (filters.searchQuery) {
			list.push({ key: 'searchQuery', label: `"${filters.searchQuery}"` });
		}

		filters.electionTypes.forEach((type) => {
			list.push({ key: 'electionTypes', label: type, value: type });
		});

		if (filters.status) {
			const statusLabel = STATUS_OPTIONS.find((s) => s.value === filters.status)?.label || filters.status;
			list.push({ key: 'status', label: statusLabel });
		}

		if (filters.dateFrom) {
			list.push({ key: 'dateFrom', label: `From: ${filters.dateFrom}` });
		}

		if (filters.dateTo) {
			list.push({ key: 'dateTo', label: `To: ${filters.dateTo}` });
		}

		if (filters.constituency) {
			list.push({ key: 'constituency', label: `Constituency: ${filters.constituency}` });
		}

		return list;
	}, [filters]);

	return {
		filters,
		filteredElections,
		activeFilters,
		activeFilterCount,
		advancedFilterCount,
		isAdvancedOpen,
		setIsAdvancedOpen,
		updateFilter,
		toggleElectionType,
		clearElectionTypes,
		selectAllElectionTypes,
		removeFilter,
		clearAllFilters,
		resetAdvancedFilters,
		resetLgaAndBelow,
		resetWard,
	};
};
